import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Send, Plus, Bell, BellOff, Users, MessageCircle, X, Paperclip, ArrowLeft,
  Trash2, LogOut, Mic, Square, Search, Pin, Reply, Forward, Edit3, Smile, Image as ImageIcon,
  Phone, Video as VideoIcon, Camera, Aperture, User as UserIcon, Users as UsersIcon, CheckSquare } from 'lucide-react';
import EmojiPicker from 'emoji-picker-react';
import { useChat, getSenderColor, formatTime, formatChatTime, formatDateSeparator, getInitials } from './useChat';
import { useCall } from './useCall';
import CallScreen from './CallScreen';
import ContactsPanel from './ContactsPanel';
import TasksPanel from './TasksPanel';
import api from './api';
import * as S from './ChatPage.styled';

function getLastPreview(chat) {
  if (!chat.lastMessage) return 'Нет сообщений';
  const { type, content, senderName } = chat.lastMessage;
  const prefix = chat.type === 'group' ? `${senderName || ''}: ` : '';
  switch (type) {
    case 'image': return prefix + '📷 Фото';
    case 'video': return prefix + '🎬 Видео';
    case 'audio': return prefix + '🎤 Аудио';
    case 'file': return prefix + '📎 Файл';
    case 'call': return prefix + '📞 Звонок';
    case 'system': return content || '';
    default: return prefix + (content || '');
  }
}

function formatBytes(b) {
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1048576).toFixed(1)} MB`;
}

function isSameDay(d1, d2) {
  return new Date(d1).toDateString() === new Date(d2).toDateString();
}

// ── Image Preview Modal ─────────────────────────────────────
function ImagePreviewModal({ src, onClose }) {
  if (!src) return null;
  return (
    <S.ModalOverlay onClick={onClose} style={{ background: 'rgba(0,0,0,0.85)' }}>
      <img src={src} alt="" onClick={e => e.stopPropagation()}
        style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 8, objectFit: 'contain' }} />
    </S.ModalOverlay>
  );
}

// ── Forward Modal ───────────────────────────────────────────
function ForwardModal({ rooms, onForward, onClose }) {
  return (
    <S.ModalOverlay onClick={onClose}>
      <S.Modal onClick={e => e.stopPropagation()}>
        <S.ModalHeader>Переслать в...<S.HeaderActionButton onClick={onClose} style={{ background: 'none', color: '#666' }}><X size={18} /></S.HeaderActionButton></S.ModalHeader>
        <S.ModalBody>
          {rooms.map(r => (
            <S.ConversationItem key={r.id} onClick={() => { onForward(r.id); onClose(); }}>
              <S.Avatar $color={getSenderColor(r.id)} style={{ width: 36, height: 36, fontSize: 13 }}>
                {r.type === 'group' ? <Users size={14} /> : getInitials(r.name)}
              </S.Avatar>
              <S.ConversationName>{r.name}</S.ConversationName>
            </S.ConversationItem>
          ))}
        </S.ModalBody>
      </S.Modal>
    </S.ModalOverlay>
  );
}

// ── Waveform Audio Player ───────────────────────────────────
function WaveformPlayer({ url, duration: initDuration }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(initDuration || 0);
  const bars = useRef(Array.from({ length: 30 }, (_, i) => 8 + ((i * 7 + 13) % 17)));

  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); } else { audioRef.current.play(); }
    setPlaying(!playing);
  };

  useEffect(() => {
    const a = new Audio(url);
    audioRef.current = a;
    a.addEventListener('timeupdate', () => { if (a.duration) setProgress(a.currentTime / a.duration); });
    a.addEventListener('loadedmetadata', () => { if (a.duration && isFinite(a.duration)) setDuration(a.duration); });
    a.addEventListener('ended', () => { setPlaying(false); setProgress(0); });
    return () => { a.pause(); a.src = ''; };
  }, [url]);

  const seek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    if (audioRef.current?.duration) { audioRef.current.currentTime = pct * audioRef.current.duration; setProgress(pct); }
  };

  const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 200 }}>
      <button onClick={toggle} style={{
        width: 36, height: 36, borderRadius: '50%', background: '#7B2FF7', color: '#fff',
        border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        {playing ? '⏸' : '▶'}
      </button>
      <div onClick={seek} style={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, cursor: 'pointer', height: 32 }}>
        {bars.current.map((h, i) => (
          <div key={i} style={{
            width: 3, height: h, borderRadius: 2, transition: 'background 0.1s',
            background: i / bars.current.length <= progress ? '#1976d2' : '#ccc',
          }} />
        ))}
      </div>
      <span style={{ fontSize: 12, color: '#667781', minWidth: 36, textAlign: 'right' }}>
        {fmt(playing && audioRef.current ? audioRef.current.currentTime : duration)}
      </span>
    </div>
  );
}

// ── Main Chat Page ──────────────────────────────────────────
export default function ChatPage({ user, onLogout }) {
  const chat = useChat(user);
  const call = useCall(chat.socket, user);
  const [showNewChat, setShowNewChat] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showContacts, setShowContacts] = useState(false);
  const [showTasks, setShowTasks] = useState(false);
  const [currentUser, setCurrentUser] = useState(user);
  const [showMobileMessages, setShowMobileMessages] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [showEmoji, setShowEmoji] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [forwardMsg, setForwardMsg] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);

  // Сообщение → задача
  const [taskToast, setTaskToast] = useState(null);
  const taskToastTimer = useRef(null);
  useEffect(() => () => clearTimeout(taskToastTimer.current), []);
  const showTaskToast = (text) => {
    setTaskToast(text);
    clearTimeout(taskToastTimer.current);
    taskToastTimer.current = setTimeout(() => setTaskToast(null), 2500);
  };
  const createTaskFromMessage = async (msg, assigneeId = null) => {
    const title = (msg.content || '').trim().slice(0, 200);
    if (!title) return;
    const room = chat.selectedRoom;
    const payload = { title };
    // Привязка задачи к переписке
    if (room && !String(msg.id).startsWith('temp_')) {
      payload.sourceChatId = room.id;
      payload.sourceMessageId = msg.id;
      payload.chatTitle = room.name;
    }
    // Назначение исполнителя (групповой чат)
    const assigned = assigneeId && assigneeId !== user.id;
    if (assigned) {
      payload.assigneeId = assigneeId;
      payload.chatId = room.id;
    }
    try {
      await api.post('/tasks', payload);
      showTaskToast(assigned ? 'Задача назначена' : 'Задача создана');
    } catch {
      showTaskToast('Не удалось создать задачу');
    }
  };

  // Подменю выбора исполнителя «Себе / Для {участник}» для групповых чатов
  const [assignMenu, setAssignMenu] = useState(null); // { x, y, msg, members: null|[] }
  const openTaskAction = async (e, msg) => {
    const room = chat.selectedRoom;
    if (!room || room.type !== 'group') { createTaskFromMessage(msg); return; }
    const x = Math.min(e.clientX, window.innerWidth - 220);
    const y = Math.min(e.clientY, window.innerHeight - 260);
    setAssignMenu({ x, y, msg, members: null });
    try {
      const { data } = await api.get(`/chats/${room.id}`);
      const members = (data.chat?.members || [])
        .map(m => m.User || m.user)
        .filter(u => u && u.id !== user.id);
      setAssignMenu(prev => prev && prev.msg.id === msg.id ? { ...prev, members } : prev);
    } catch {
      setAssignMenu(prev => prev && prev.msg.id === msg.id ? { ...prev, members: [] } : prev);
    }
  };

  // Narrow window detection — hide sidebar below 900px like Telegram
  const [isNarrow, setIsNarrow] = useState(() => window.innerWidth < 900);
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Floating date indicator (Telegram-style)
  const [floatingDate, setFloatingDate] = useState(null);
  const floatingDateTimeout = useRef(null);
  const messagesListRef = useRef(null);

  // Recording
  const [recording, setRecording] = useState(false);
  const [recordDuration, setRecordDuration] = useState(0);
  const mediaRecorderRef = useRef(null);
  const recordChunksRef = useRef([]);
  const recordTimerRef = useRef(null);
  const recordStartRef = useRef(null);

  const handleSelectRoom = (room) => { chat.setSelectedRoom(room); setShowMobileMessages(true); };
  const handleBack = () => { chat.setSelectedRoom(null); setShowMobileMessages(false); };

  const handleFileChange = (e) => { const f = e.target.files[0]; if (f) chat.uploadFile(f); e.target.value = ''; };

  // Context menu
  const handleContextMenu = (e, msg) => {
    e.preventDefault();
    if (msg.isDeleted || msg.type === 'system') return;
    const x = Math.min(e.clientX, window.innerWidth - 220);
    const y = Math.min(e.clientY, window.innerHeight - 250);
    setContextMenu({ x, y, msg });
  };

  // Emoji
  const onEmojiClick = (emojiData) => {
    chat.setMessageText(prev => prev + emojiData.emoji);
    setShowEmoji(false);
    chat.textareaRef.current?.focus();
  };

  // Drag & drop
  const handleDragEnter = (e) => { e.preventDefault(); dragCounter.current++; setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); dragCounter.current--; if (dragCounter.current === 0) setIsDragging(false); };
  const handleDragOver = (e) => { e.preventDefault(); };
  const handleDrop = (e) => {
    e.preventDefault(); dragCounter.current = 0; setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && chat.selectedRoom) chat.uploadFile(file);
  };

  // Floating date on scroll
  const handleMessagesScroll = useCallback(() => {
    const list = messagesListRef.current;
    if (!list) return;

    // Find the topmost visible message's date
    const listTop = list.getBoundingClientRect().top;
    const items = list.querySelectorAll('[data-msg-date]');
    let topDate = null;
    for (const el of items) {
      const r = el.getBoundingClientRect();
      if (r.bottom > listTop + 40) {
        topDate = el.getAttribute('data-msg-date');
        break;
      }
    }
    if (topDate) setFloatingDate(topDate);

    // Hide after 1.5s of no scrolling
    if (floatingDateTimeout.current) clearTimeout(floatingDateTimeout.current);
    floatingDateTimeout.current = setTimeout(() => setFloatingDate(null), 1500);
  }, []);

  // Paste image
  useEffect(() => {
    const handler = (e) => {
      const items = e.clipboardData?.items;
      if (!items || !chat.selectedRoom) return;
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) chat.uploadFile(file);
          break;
        }
      }
    };
    document.addEventListener('paste', handler);
    return () => document.removeEventListener('paste', handler);
  }, [chat.selectedRoom, chat.uploadFile]);

  // Voice recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : 'audio/webm';
      const recorder = new MediaRecorder(stream, { mimeType });
      recordChunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) recordChunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(recordChunksRef.current, { type: mimeType });
        const ext = mimeType.includes('mp4') ? 'm4a' : 'webm';
        const dur = Math.round((Date.now() - recordStartRef.current) / 1000);
        const file = new File([blob], `voice_${Date.now()}.${ext}`, { type: mimeType });
        const formData = new FormData(); formData.append('file', file); formData.append('duration', String(dur));
        try { await api.post(`/chats/${chat.selectedRoom.id}/messages/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 }); chat.loadRooms(); } catch {}
      };
      mediaRecorderRef.current = recorder;
      recordStartRef.current = Date.now();
      recorder.start(100);
      setRecording(true); setRecordDuration(0);
      recordTimerRef.current = setInterval(() => setRecordDuration(prev => prev + 1), 1000);
    } catch { alert('Не удалось получить доступ к микрофону'); }
  };
  const stopRecording = () => { mediaRecorderRef.current?.state !== 'inactive' && mediaRecorderRef.current?.stop(); clearInterval(recordTimerRef.current); setRecording(false); setRecordDuration(0); };
  const cancelRecording = () => {
    if (mediaRecorderRef.current?.state !== 'inactive') { mediaRecorderRef.current.ondataavailable = null; mediaRecorderRef.current.onstop = () => mediaRecorderRef.current?.stream?.getTracks().forEach(t => t.stop()); mediaRecorderRef.current.stop(); }
    clearInterval(recordTimerRef.current); setRecording(false); setRecordDuration(0);
  };
  const fmtRec = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  // Video circle recording (кружок)
  const CIRCLE_MAX_SEC = 60;
  const [circleRecording, setCircleRecording] = useState(false);
  const [circleDuration, setCircleDuration] = useState(0);
  const [mediaError, setMediaError] = useState(null);
  const circleRecorderRef = useRef(null);
  const circleStreamRef = useRef(null);
  const circleChunksRef = useRef([]);
  const circleTimerRef = useRef(null);
  const circleStartRef = useRef(null);
  const circleCancelledRef = useRef(false);
  const circlePreviewRef = useRef(null);
  const mediaErrorTimeout = useRef(null);

  const showMediaError = (text) => {
    setMediaError(text);
    clearTimeout(mediaErrorTimeout.current);
    mediaErrorTimeout.current = setTimeout(() => setMediaError(null), 5000);
  };

  const startCircleRecording = async () => {
    if (circleRecording) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      showMediaError('Камера недоступна: браузер не поддерживает запись или страница открыта не по HTTPS');
      return;
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 480 }, height: { ideal: 480 } },
        audio: true,
      });
    } catch (err) {
      const msg = err?.name === 'NotAllowedError'
        ? 'Нет доступа к камере: разрешите доступ в настройках браузера'
        : err?.name === 'NotFoundError'
          ? 'Камера не найдена'
          : 'Не удалось получить доступ к камере/микрофону';
      showMediaError(msg);
      return;
    }
    try {
      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus') ? 'video/webm;codecs=vp8,opus'
        : MediaRecorder.isTypeSupported('video/webm') ? 'video/webm'
        : MediaRecorder.isTypeSupported('video/mp4') ? 'video/mp4' : '';
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      const actualType = recorder.mimeType || mimeType || 'video/webm';
      circleChunksRef.current = [];
      circleCancelledRef.current = false;
      recorder.ondataavailable = (e) => { if (e.data.size > 0) circleChunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        circleStreamRef.current = null;
        if (circleCancelledRef.current || circleChunksRef.current.length === 0) return;
        const blob = new Blob(circleChunksRef.current, { type: actualType });
        const ext = actualType.includes('mp4') ? 'mp4' : 'webm';
        const dur = Math.round((Date.now() - circleStartRef.current) / 1000);
        const file = new File([blob], `circle_${Date.now()}.${ext}`, { type: actualType });
        const formData = new FormData();
        formData.append('file', file);
        formData.append('duration', String(dur));
        formData.append('isCircle', 'true');
        formData.append('width', '480');
        formData.append('height', '480');
        try {
          await api.post(`/chats/${chat.selectedRoom.id}/messages/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
          chat.loadRooms();
        } catch {
          showMediaError('Не удалось отправить видео-кружок');
        }
      };
      circleRecorderRef.current = recorder;
      circleStreamRef.current = stream;
      circleStartRef.current = Date.now();
      recorder.start(100);
      setCircleRecording(true);
      setCircleDuration(0);
      circleTimerRef.current = setInterval(() => {
        setCircleDuration(prev => {
          if (prev + 1 >= CIRCLE_MAX_SEC) stopCircleRecording();
          return prev + 1;
        });
      }, 1000);
    } catch {
      stream.getTracks().forEach(t => t.stop());
      showMediaError('Запись видео не поддерживается этим браузером');
    }
  };

  const stopCircleRecording = () => {
    if (circleRecorderRef.current && circleRecorderRef.current.state !== 'inactive') circleRecorderRef.current.stop();
    clearInterval(circleTimerRef.current);
    setCircleRecording(false);
    setCircleDuration(0);
  };

  const cancelCircleRecording = () => {
    circleCancelledRef.current = true;
    stopCircleRecording();
  };

  // Attach live preview stream to the round <video>
  useEffect(() => {
    if (circleRecording && circlePreviewRef.current && circleStreamRef.current) {
      circlePreviewRef.current.srcObject = circleStreamRef.current;
      circlePreviewRef.current.play().catch(() => {});
    }
  }, [circleRecording]);

  // Cleanup on unmount
  useEffect(() => () => {
    clearInterval(circleTimerRef.current);
    clearTimeout(mediaErrorTimeout.current);
    circleStreamRef.current?.getTracks().forEach(t => t.stop());
  }, []);

  const typingText = Object.values(chat.typingUsers).join(', ');
  const pinnedMessage = chat.messages.find(m => m.isPinned && !m.isDeleted);

  return (
    <S.Container onClick={() => { setContextMenu(null); setShowEmoji(false); }}>
      <style>{`
        .trk-msg-task { opacity: 0; transition: opacity 0.15s; }
        .trk-bubble:hover .trk-msg-task { opacity: 1; }
      `}</style>
      <S.Sidebar
        $showChat={showMobileMessages}
        style={isNarrow && chat.selectedRoom ? { display: 'none' } : isNarrow ? { width: '100%', minWidth: 0 } : undefined}
      >
        <S.SidebarHeader>
          <div
            onClick={() => setShowProfile(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
            title="Профиль"
          >
            <div style={{
              width: 32, height: 32, borderRadius: '50%',
              background: currentUser.avatar ? `url(${currentUser.avatar}) center/cover` : 'rgba(255,255,255,0.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'white', fontSize: 13, fontWeight: 600, flexShrink: 0,
              border: '2px solid rgba(255,255,255,0.4)',
            }}>
              {!currentUser.avatar && getInitials(currentUser.displayName)}
            </div>
            <S.SidebarTitle>tërka</S.SidebarTitle>
          </div>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <S.SoundToggleButton $active={chat.soundEnabled} onClick={() => chat.setSoundEnabled(!chat.soundEnabled)}>
              {chat.soundEnabled ? <Bell size={16} /> : <BellOff size={16} />}
            </S.SoundToggleButton>
            <S.HeaderActionButton onClick={() => setShowContacts(true)} title="Контакты"><UsersIcon size={16} /></S.HeaderActionButton>
            <S.HeaderActionButton onClick={() => setShowTasks(true)} title="Задачи"><CheckSquare size={16} /></S.HeaderActionButton>
            <S.NewChatButton onClick={() => setShowNewChat(true)}><Plus size={20} /></S.NewChatButton>
            <S.HeaderActionButton onClick={onLogout} title="Выйти"><LogOut size={16} /></S.HeaderActionButton>
          </div>
        </S.SidebarHeader>
        <S.ConversationsList>
          {chat.rooms.map(room => (
            <S.ConversationItem key={room.id} $active={chat.selectedRoom?.id === room.id} onClick={() => handleSelectRoom(room)}>
              <S.Avatar $color={room.type === 'saved' ? '#6C7BFF' : getSenderColor(room.id)}>
                {room.avatar ? <img src={room.avatar} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                  : room.type === 'saved' ? '🔖' : room.type === 'group' ? <Users size={18} /> : getInitials(room.name)}
              </S.Avatar>
              <S.ConversationInfo>
                <S.ConversationHeader>
                  <S.ConversationName>{room.name}{room.isMuted && <BellOff size={12} style={{ opacity: 0.5 }} />}</S.ConversationName>
                  <S.ConversationTime $unread={room.unreadCount > 0}>{room.lastMessage ? formatTime(room.lastMessage.createdAt) : ''}</S.ConversationTime>
                </S.ConversationHeader>
                <S.ConversationPreview>{getLastPreview(room)}</S.ConversationPreview>
              </S.ConversationInfo>
              {room.unreadCount > 0 && <S.UnreadBadge>{room.unreadCount}</S.UnreadBadge>}
            </S.ConversationItem>
          ))}
        </S.ConversationsList>
      </S.Sidebar>

      <S.MessagesArea
        $show={showMobileMessages}
        style={isNarrow && !chat.selectedRoom ? { display: 'none' } : undefined}
      >
        {!chat.selectedRoom ? (
          <S.EmptyState>
            <S.EmptyIcon><MessageCircle size={48} /></S.EmptyIcon>
            <p style={{ fontSize: 16, fontWeight: 500 }}>tërka messenger</p>
            <p style={{ fontSize: 13, marginTop: 4 }}>или создайте новый</p>
          </S.EmptyState>
        ) : (
          <>
            <S.MessagesHeader>
              {(showMobileMessages || isNarrow) && (
                <button
                  onClick={() => { chat.setSelectedRoom(null); setShowMobileMessages(false); }}
                  style={{ background: 'transparent', border: 'none', color: 'white', cursor: 'pointer', padding: 8, display: 'flex', alignItems: 'center' }}
                >
                  <ArrowLeft size={20} />
                </button>
              )}
              <S.HeaderInfo>
                <S.HeaderName>{chat.selectedRoom.name}</S.HeaderName>
                <S.HeaderContact>
                  <S.MemberCount>
                    {chat.selectedRoom.type === 'direct' && chat.selectedRoom.directUser
                      ? (chat.selectedRoom.directUser.isOnline ? '● в сети' : 'не в сети')
                      : <><Users size={12} /> {chat.selectedRoom.memberCount} участников</>}
                  </S.MemberCount>
                </S.HeaderContact>
              </S.HeaderInfo>
              {chat.selectedRoom.type === 'direct' && chat.selectedRoom.directUser && !call.isActive && (
                <>
                  <S.HeaderActionButton
                    onClick={() => call.initiateCall(chat.selectedRoom.directUser, false, chat.selectedRoom.id)}
                    title="Аудиозвонок"
                  >
                    <Phone size={16} />
                  </S.HeaderActionButton>
                  <S.HeaderActionButton
                    onClick={() => call.initiateCall(chat.selectedRoom.directUser, true, chat.selectedRoom.id)}
                    title="Видеозвонок"
                  >
                    <VideoIcon size={16} />
                  </S.HeaderActionButton>
                </>
              )}
              <S.HeaderActionButton onClick={chat.toggleSearch} title="Поиск"><Search size={16} /></S.HeaderActionButton>
              <S.MuteButton onClick={() => chat.toggleMute(chat.selectedRoom.id)}>
                {chat.selectedRoom.isMuted ? <BellOff size={18} /> : <Bell size={18} />}
              </S.MuteButton>
            </S.MessagesHeader>

            {/* Search bar */}
            {chat.showSearch && (
              <div style={{ padding: '8px 16px', background: '#fff', borderBottom: '1px solid #e1e5e9', display: 'flex', gap: 8 }}>
                <input value={chat.searchTerm} onChange={e => chat.searchMessages(e.target.value)}
                  placeholder="Поиск сообщений..." autoFocus
                  style={{ flex: 1, padding: '8px 12px', border: '1px solid #e1e5e9', borderRadius: 20, fontSize: 14, outline: 'none' }} />
                <button onClick={chat.toggleSearch} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#666' }}><X size={18} /></button>
              </div>
            )}

            {/* Pinned message */}
            {pinnedMessage && (
              <div style={{ padding: '8px 16px', background: '#fff8e1', borderBottom: '1px solid #ffe082', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <Pin size={14} style={{ color: '#f57c00' }} />
                <div style={{ flex: 1, fontSize: 13 }}>
                  <span style={{ fontWeight: 600, color: '#f57c00' }}>Закреплённое: </span>
                  <span style={{ color: '#333' }}>{pinnedMessage.content?.slice(0, 80)}</span>
                </div>
              </div>
            )}

            {/* Drag overlay */}
            <div
              style={{ flex: 1, position: 'relative', display: 'flex', flexDirection: 'column', minHeight: 0 }}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              {isDragging && (
                <div style={{
                  position: 'absolute', inset: 0,
                  background: 'rgba(123, 47, 247, 0.12)',
                  border: '3px dashed #7B2FF7',
                  borderRadius: 8, zIndex: 50,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  pointerEvents: 'none', margin: 8,
                }}>
                  <div style={{ color: '#7B2FF7', fontSize: 18, fontWeight: 600, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                    <ImageIcon size={40} style={{ opacity: 0.8 }} />
                    Перетащите файл сюда
                  </div>
                </div>
              )}
              <S.FloatingDate $visible={!!floatingDate}>{floatingDate}</S.FloatingDate>
              <S.MessagesList ref={messagesListRef} onScroll={handleMessagesScroll}>
              {chat.messages.map((msg, idx) => {
                if (msg.type === 'system') return <S.SystemMessage key={msg.id}>{msg.content}</S.SystemMessage>;

                // Call event — custom card with Перезвонить button
                if (msg.type === 'call') {
                  const meta = msg.metadata || {};
                  const wasMe = msg.senderId === user.id; // I initiated the log (usually caller or decliner)
                  const isVideo = !!meta.isVideo;
                  const status = meta.status || 'ended';
                  const duration = Number(meta.duration || 0);

                  let label, color, arrow;
                  if (status === 'ended') {
                    label = wasMe ? 'Исходящий звонок' : 'Входящий звонок';
                    color = '#7B2FF7';
                    arrow = wasMe ? '↗' : '↙';
                  } else if (status === 'missed') {
                    label = wasMe ? 'Без ответа' : 'Пропущенный вызов';
                    color = wasMe ? '#888' : '#dc3545';
                    arrow = wasMe ? '↗' : '↙';
                  } else if (status === 'declined') {
                    label = wasMe ? 'Отклонён вами' : 'Отклонён';
                    color = '#888';
                    arrow = wasMe ? '↘' : '↗';
                  } else if (status === 'busy') {
                    label = 'Занято';
                    color = '#888';
                    arrow = '↗';
                  } else {
                    label = 'Звонок';
                    color = '#888';
                    arrow = '';
                  }

                  const fmt = (s) => {
                    if (!s) return '';
                    const h = Math.floor(s / 3600);
                    const m = Math.floor((s % 3600) / 60);
                    const sec = s % 60;
                    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
                    return `${m}:${String(sec).padStart(2, '0')}`;
                  };

                  const showDateSep2 = idx === 0 || !isSameDay(msg.createdAt, chat.messages[idx - 1].createdAt);

                  return (
                    <React.Fragment key={msg.id}>
                      {showDateSep2 && <S.DateSeparator data-msg-date={formatDateSeparator(msg.createdAt)}>{formatDateSeparator(msg.createdAt)}</S.DateSeparator>}
                      <div style={{
                        alignSelf: 'center',
                        background: 'white',
                        borderRadius: 16,
                        padding: '10px 16px',
                        margin: '4px 0',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        maxWidth: '85%',
                      }}>
                        <div style={{
                          width: 36, height: 36, borderRadius: '50%',
                          background: `${color}15`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          flexShrink: 0,
                        }}>
                          {isVideo ? <VideoIcon size={18} color={color} /> : <Phone size={18} color={color} />}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: '#333' }}>
                            <span style={{ color, marginRight: 4 }}>{arrow}</span>
                            {isVideo ? 'Видео: ' : ''}{label}
                          </div>
                          <div style={{ fontSize: 12, color: '#888' }}>
                            {duration > 0 ? fmt(duration) + ' · ' : ''}
                            {formatChatTime(msg.createdAt)}
                          </div>
                        </div>
                        {chat.selectedRoom?.type === 'direct' && chat.selectedRoom?.directUser && !call.isActive && (
                          <button
                            onClick={() => call.initiateCall(chat.selectedRoom.directUser, isVideo, chat.selectedRoom.id)}
                            style={{
                              background: 'linear-gradient(135deg, #7B2FF7, #E040A0)',
                              color: 'white', border: 'none', borderRadius: 8,
                              padding: '6px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 500,
                              display: 'flex', alignItems: 'center', gap: 4,
                              flexShrink: 0,
                            }}
                            title="Перезвонить"
                          >
                            {isVideo ? <VideoIcon size={14} /> : <Phone size={14} />}
                            Перезвонить
                          </button>
                        )}
                      </div>
                    </React.Fragment>
                  );
                }

                const showDateSep = idx === 0 || !isSameDay(msg.createdAt, chat.messages[idx - 1].createdAt);
                const isOutbound = msg.senderId === user.id;
                const isHighlighted = chat.searchResults.includes(msg.id);

                if (msg.isDeleted) {
                  return (
                    <React.Fragment key={msg.id}>
                      {showDateSep && <S.DateSeparator data-msg-date={formatDateSeparator(msg.createdAt)}>{formatDateSeparator(msg.createdAt)}</S.DateSeparator>}
                      <S.MessageBubble $outbound={isOutbound} style={{ opacity: 0.6 }}>
                        <S.MessageText style={{ fontStyle: 'italic', color: '#888' }}>Сообщение удалено</S.MessageText>
                      </S.MessageBubble>
                    </React.Fragment>
                  );
                }

                return (
                  <React.Fragment key={msg.id}>
                    {showDateSep && <S.DateSeparator data-msg-date={formatDateSeparator(msg.createdAt)}>{formatDateSeparator(msg.createdAt)}</S.DateSeparator>}
                    <S.MessageBubble className="trk-bubble" $outbound={isOutbound} onContextMenu={e => handleContextMenu(e, msg)}
                      style={isHighlighted ? { boxShadow: '0 0 0 2px #1976d2' } : undefined}>

                      {/* Hover: создать задачу из сообщения */}
                      {msg.content && msg.type === 'text' && (
                        <button
                          className="trk-msg-task"
                          title="Создать задачу из сообщения"
                          onClick={(e) => { e.stopPropagation(); openTaskAction(e, msg); }}
                          style={{
                            position: 'absolute', top: -8,
                            [isOutbound ? 'left' : 'right']: -8,
                            width: 22, height: 22, borderRadius: '50%',
                            border: '1px solid #e1e5e9', background: 'white', color: '#2AAE9C',
                            cursor: 'pointer', fontSize: 12, lineHeight: 1, padding: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.15)', zIndex: 2,
                          }}
                        >
                          ✓
                        </button>
                      )}

                      {/* Pin indicator */}
                      {msg.isPinned && <div style={{ fontSize: 11, color: '#f57c00', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 2 }}><Pin size={10} /> Закреплено</div>}

                      {/* Sender name */}
                      {!isOutbound && chat.selectedRoom.type === 'group' && (
                        <S.SenderName $color={getSenderColor(msg.senderId)}>{msg.sender?.displayName || 'Пользователь'}</S.SenderName>
                      )}

                      {/* Reply quote */}
                      {msg.replyMessage && (
                        <div style={{ borderLeft: '3px solid #1976d2', paddingLeft: 8, marginBottom: 4, opacity: 0.7 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#7B2FF7' }}>{msg.replyMessage.sender?.displayName}</div>
                          <div style={{ fontSize: 12, color: '#667781' }}>{msg.replyMessage.content?.slice(0, 60)}</div>
                        </div>
                      )}

                      {/* Image */}
                      {msg.type === 'image' && msg.fileInfo && (
                        <S.MessageImage src={msg.fileInfo.url} alt="" onClick={() => setPreviewImage(msg.fileInfo.url)} />
                      )}

                      {/* Video */}
                      {msg.type === 'video' && msg.fileInfo && (msg.fileInfo.isCircle ? (
                        <video
                          src={msg.fileInfo.url}
                          playsInline
                          style={{ width: 220, height: 220, borderRadius: '50%', objectFit: 'cover', marginBottom: 4, cursor: 'pointer', background: '#000' }}
                          onClick={e => { const v = e.currentTarget; if (v.paused) { v.play(); } else { v.pause(); } }}
                        />
                      ) : (
                        <video controls src={msg.fileInfo.url} style={{ maxWidth: 280, borderRadius: 8, marginBottom: 4 }} />
                      )
                      )}

                      {/* Audio — waveform */}
                      {msg.type === 'audio' && msg.fileInfo && (
                        <WaveformPlayer url={msg.fileInfo.url} duration={msg.fileInfo.duration} />
                      )}

                      {/* File */}
                      {msg.type === 'file' && msg.fileInfo && (
                        <S.FileAttachment href={msg.fileInfo.url} target="_blank" rel="noreferrer">
                          📎 <div><div>{msg.fileInfo.name}</div><small style={{ color: '#667781' }}>{formatBytes(msg.fileInfo.size)}</small></div>
                        </S.FileAttachment>
                      )}

                      {/* Forwarded label */}
                      {msg.forwardedFrom && (
                        <div style={{ fontSize: 11, color: '#667781', fontStyle: 'italic', marginBottom: 2 }}>↗ Пересланное сообщение</div>
                      )}

                      {msg.content && <S.MessageText>{msg.content}</S.MessageText>}

                      <S.MessageMeta>
                        {msg.isEdited && <span style={{ fontSize: 11, color: '#667781' }}>ред.</span>}
                        <S.MessageTime>{formatChatTime(msg.createdAt)}</S.MessageTime>
                        {isOutbound && (
                          <S.MessageStatus $status={msg.status === 'failed' ? 'failed' : 'sent'}>
                            {msg.status === 'sending' ? '🕐' : msg.status === 'failed' ? '!' : '✓✓'}
                          </S.MessageStatus>
                        )}
                      </S.MessageMeta>
                    </S.MessageBubble>
                  </React.Fragment>
                );
              })}
              <div ref={chat.messagesEndRef} />
              </S.MessagesList>
            </div>

            {typingText && <S.TypingIndicator>{typingText} печатает...</S.TypingIndicator>}

            {/* Reply / Edit bar */}
            {(chat.replyTo || chat.editingMessage) && (
              <div style={{ padding: '8px 16px', background: '#fff', borderTop: '1px solid #e1e5e9', display: 'flex', alignItems: 'center', gap: 8 }}>
                {chat.replyTo && <Reply size={16} style={{ color: '#7B2FF7', flexShrink: 0 }} />}
                {chat.editingMessage && <Edit3 size={16} style={{ color: '#f57c00', flexShrink: 0 }} />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: chat.editingMessage ? '#f57c00' : '#1976d2' }}>
                    {chat.editingMessage ? 'Редактирование' : chat.replyTo.sender?.displayName}
                  </div>
                  <div style={{ fontSize: 12, color: '#667781', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {(chat.editingMessage || chat.replyTo)?.content?.slice(0, 60)}
                  </div>
                </div>
                <button onClick={() => { chat.cancelEditing(); chat.cancelReply(); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#666', padding: 4 }}><X size={16} /></button>
              </div>
            )}

            {/* Input area */}
            <S.MessageInputArea>
              {recording ? (
                <>
                  <S.CancelRecordButton onClick={cancelRecording}><X size={18} /> Отмена</S.CancelRecordButton>
                  <S.RecordingArea><S.RecordingDot /><S.RecordingTimer>{fmtRec(recordDuration)}</S.RecordingTimer></S.RecordingArea>
                  <S.SendButton onClick={stopRecording}><Send size={20} /></S.SendButton>
                </>
              ) : (
                <>
                  <S.AttachButton><Paperclip size={20} /><input type="file" onChange={handleFileChange} accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.zip,.rar" /></S.AttachButton>
                  <div style={{ position: 'relative', flex: 1 }}>
                    <S.MessageTextarea ref={chat.textareaRef} value={chat.messageText}
                      onChange={e => { chat.setMessageText(e.target.value); chat.sendTyping(); }}
                      onKeyDown={chat.handleKeyPress}
                      placeholder={chat.editingMessage ? 'Редактирование...' : 'Введите сообщение...'} rows={1} />
                    {showEmoji && (
                      <div style={{ position: 'absolute', bottom: 50, left: 0, zIndex: 50 }} onClick={e => e.stopPropagation()}>
                        <EmojiPicker onEmojiClick={onEmojiClick} width={340} height={400} lazyLoadEmojis searchPlaceHolder="Поиск..." />
                      </div>
                    )}
                  </div>
                  <button onClick={e => { e.stopPropagation(); setShowEmoji(!showEmoji); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8, color: '#666' }} title="Эмодзи"><Smile size={22} /></button>
                  {chat.messageText.trim() ? (
                    <S.SendButton onClick={chat.sendMessage}><Send size={20} /></S.SendButton>
                  ) : (
                    <>
                      <S.CircleButton onClick={startCircleRecording} title="Видео-кружок"><Aperture size={20} /></S.CircleButton>
                      <S.MicButton onClick={startRecording} title="Голосовое"><Mic size={20} /></S.MicButton>
                    </>
                  )}
                </>
              )}
            </S.MessageInputArea>
          </>
        )}
      </S.MessagesArea>

      {/* Video circle recording overlay */}
      {circleRecording && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.75)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 20,
        }} onClick={e => e.stopPropagation()}>
          <video ref={circlePreviewRef} muted playsInline style={{
            width: 280, height: 280, borderRadius: '50%', objectFit: 'cover',
            border: '4px solid #00a884', background: '#000', transform: 'scaleX(-1)',
          }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'white', fontSize: 18 }}>
            <S.RecordingDot /> {fmtRec(circleDuration)} / {fmtRec(CIRCLE_MAX_SEC)}
          </div>
          <div style={{ display: 'flex', gap: 16 }}>
            <button onClick={cancelCircleRecording} style={{
              background: 'rgba(255,255,255,0.15)', color: 'white', border: 'none', borderRadius: 24,
              padding: '12px 24px', fontSize: 15, cursor: 'pointer',
            }}><X size={16} style={{ verticalAlign: -3 }} /> Отмена</button>
            <button onClick={stopCircleRecording} style={{
              background: '#00a884', color: 'white', border: 'none', borderRadius: 24,
              padding: '12px 24px', fontSize: 15, cursor: 'pointer',
            }}><Send size={16} style={{ verticalAlign: -3 }} /> Отправить</button>
          </div>
        </div>
      )}

      {/* Media error toast */}
      {mediaError && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 1100,
          background: '#323232', color: 'white', padding: '12px 20px', borderRadius: 10,
          fontSize: 14, boxShadow: '0 4px 16px rgba(0,0,0,0.3)', maxWidth: '90vw',
        }}>{mediaError}</div>
      )}

      {/* Context menu */}
      {contextMenu && (
        <S.ContextMenu style={{ left: contextMenu.x, top: contextMenu.y }} onClick={e => e.stopPropagation()}>
          <S.ContextMenuItem onClick={() => { chat.startReply(contextMenu.msg); setContextMenu(null); }}>
            <Reply size={16} /> Ответить
          </S.ContextMenuItem>
          <S.ContextMenuItem onClick={() => { setForwardMsg(contextMenu.msg); setContextMenu(null); }}>
            <Forward size={16} /> Переслать
          </S.ContextMenuItem>
          {contextMenu.msg.senderId === user.id && contextMenu.msg.type === 'text' && (
            <S.ContextMenuItem onClick={() => { chat.startEditing(contextMenu.msg); setContextMenu(null); }}>
              <Edit3 size={16} /> Редактировать
            </S.ContextMenuItem>
          )}
          <S.ContextMenuItem onClick={() => { chat.pinMessage(contextMenu.msg.id); setContextMenu(null); }}>
            <Pin size={16} /> {contextMenu.msg.isPinned ? 'Открепить' : 'Закрепить'}
          </S.ContextMenuItem>
          {contextMenu.msg.content && contextMenu.msg.type === 'text' && (
            <S.ContextMenuItem onClick={(e) => { const m = contextMenu.msg; setContextMenu(null); openTaskAction(e, m); }}>
              <CheckSquare size={16} /> В задачу{chat.selectedRoom?.type === 'group' ? '…' : ''}
            </S.ContextMenuItem>
          )}
          {(contextMenu.msg.senderId === user.id || chat.selectedRoom?.role === 'owner') && (
            <S.ContextMenuItem $danger onClick={() => { chat.deleteMessage(contextMenu.msg.id); setContextMenu(null); }}>
              <Trash2 size={16} /> Удалить
            </S.ContextMenuItem>
          )}
        </S.ContextMenu>
      )}

      {/* Подменю «В задачу: Себе / Для {участник}» */}
      {assignMenu && (
        <>
          <div
            onClick={() => setAssignMenu(null)}
            onContextMenu={(e) => { e.preventDefault(); setAssignMenu(null); }}
            style={{ position: 'fixed', inset: 0, zIndex: 149 }}
          />
          <div style={{
            position: 'fixed', left: assignMenu.x, top: assignMenu.y, zIndex: 150,
            background: 'white', border: '1px solid #e1e5e9', borderRadius: 10,
            boxShadow: '0 6px 20px rgba(0,0,0,0.15)', minWidth: 180, maxHeight: 240,
            overflowY: 'auto', padding: 6,
          }}>
            <div style={{ padding: '5px 10px 3px', fontSize: 11, color: '#adb5bd', fontWeight: 600 }}>В задачу</div>
            <button
              onClick={() => { createTaskFromMessage(assignMenu.msg); setAssignMenu(null); }}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, borderRadius: 6 }}
            >
              Себе
            </button>
            {assignMenu.members === null && (
              <div style={{ padding: '6px 10px', fontSize: 12, color: '#adb5bd' }}>Загрузка участников…</div>
            )}
            {(assignMenu.members || []).map(m => (
              <button
                key={m.id}
                onClick={() => { createTaskFromMessage(assignMenu.msg, m.id); setAssignMenu(null); }}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px',
                  border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, borderRadius: 6,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}
              >
                Для {m.displayName || m.username}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Forward modal */}
      {forwardMsg && (
        <ForwardModal
          rooms={chat.rooms.filter(r => r.id !== chat.selectedRoom?.id && r.type !== 'saved')}
          onForward={(targetId) => chat.forwardMessage(forwardMsg.id, targetId)}
          onClose={() => setForwardMsg(null)}
        />
      )}

      {/* Image preview */}
      <ImagePreviewModal src={previewImage} onClose={() => setPreviewImage(null)} />

      {/* New chat modal */}
      {showNewChat && <NewChatModal onClose={() => setShowNewChat(false)} onCreateRoom={chat.createRoom} />}

      {/* Profile modal */}
      {showProfile && (
        <ProfileModal
          user={currentUser}
          onClose={() => setShowProfile(false)}
          onUpdate={(u) => setCurrentUser(u)}
          onLogout={onLogout}
        />
      )}

      {/* Tasks panel */}
      {showTasks && (
        <TasksPanel
          onClose={() => setShowTasks(false)}
          currentUserId={user.id}
          onOpenChat={(chatId) => {
            setShowTasks(false);
            const room = chat.rooms.find(r => r.id === chatId);
            if (room) {
              handleSelectRoom(room);
            } else {
              chat.loadRooms();
              chat.setSelectedRoom({ id: chatId, type: 'direct' });
              setShowMobileMessages(true);
            }
          }}
        />
      )}

      {/* Contacts panel */}
      {showContacts && (
        <ContactsPanel
          onClose={() => setShowContacts(false)}
          onStartChat={(chatId) => {
            chat.loadRooms();
            setTimeout(() => {
              const rooms = chat.rooms.find(r => r.id === chatId);
              if (rooms) chat.setSelectedRoom(rooms);
              else chat.setSelectedRoom({ id: chatId, type: 'direct' });
            }, 300);
          }}
        />
      )}

      {/* Мини-тост «Задача создана» */}
      {taskToast && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          background: '#333', color: 'white', borderRadius: 20, padding: '8px 18px',
          fontSize: 13, zIndex: 200, boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
          pointerEvents: 'none',
        }}>
          {taskToast}
        </div>
      )}

      {/* Call overlay */}
      {call.isActive && <CallScreen call={call} />}
    </S.Container>
  );
}

function ProfileModal({ user, onClose, onUpdate, onLogout }) {
  const [displayName, setDisplayName] = useState(user.displayName || '');
  const [bio, setBio] = useState(user.bio || '');
  const [avatar, setAvatar] = useState(user.avatar);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [storage, setStorage] = useState({ used: user.storageUsed || 0, quota: user.storageQuota || 250 * 1024 * 1024 });
  const fileInputRef = useRef(null);

  useEffect(() => {
    api.get('/auth/me/storage').then(({ data }) => {
      if (data.success) setStorage({ used: data.used, quota: data.quota });
    }).catch(() => {});
  }, []);

  const storagePct = Math.min(100, (storage.used / storage.quota) * 100);
  const storageMB = (bytes) => (bytes / 1024 / 1024).toFixed(1);
  const overLimit = storagePct >= 90;

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Выберите изображение');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('avatar', file);
      const { data } = await api.post('/auth/me/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (data.success) {
        setAvatar(data.avatar);
        onUpdate({ ...user, avatar: data.avatar });
      }
    } catch (err) {
      alert('Не удалось загрузить аватар');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/auth/me', {
        displayName: displayName.trim(),
        bio: bio.trim(),
      });
      if (data.success) {
        onUpdate({ ...user, ...data.user });
        onClose();
      }
    } catch (err) {
      alert('Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  return (
    <S.ModalOverlay onClick={onClose}>
      <S.Modal onClick={e => e.stopPropagation()} style={{ width: 420 }}>
        <S.ModalHeader>
          Профиль
          <S.HeaderActionButton onClick={onClose} style={{ background: 'none', color: '#666' }}>
            <X size={18} />
          </S.HeaderActionButton>
        </S.ModalHeader>
        <S.ModalBody>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 24 }}>
            <div
              onClick={() => !uploading && fileInputRef.current?.click()}
              style={{
                width: 120, height: 120, borderRadius: '50%',
                background: avatar ? `url(${avatar}) center/cover` : `linear-gradient(135deg, ${getSenderColor(user.id)}, ${getSenderColor(user.id)}dd)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'white', fontSize: 40, fontWeight: 600,
                cursor: uploading ? 'wait' : 'pointer',
                position: 'relative',
                border: '4px solid rgba(123,47,247,0.2)',
                transition: 'transform 0.15s ease',
              }}
              onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
            >
              {!avatar && getInitials(user.displayName)}
              <div style={{
                position: 'absolute', bottom: 0, right: 0,
                width: 36, height: 36, borderRadius: '50%',
                background: 'linear-gradient(135deg, #7B2FF7, #E040A0)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              }}>
                <Camera size={18} color="white" />
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleAvatarUpload}
            />
            <div style={{ fontSize: 13, color: '#888', marginTop: 12 }}>
              {uploading ? 'Загрузка...' : 'Нажмите, чтобы изменить фото'}
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 13, color: '#666', marginBottom: 6 }}>Имя</label>
            <S.ModalInput
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              placeholder="Ваше имя"
            />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 13, color: '#666', marginBottom: 6 }}>Логин</label>
            <S.ModalInput value={`@${user.username}`} disabled style={{ color: '#999' }} />
          </div>

          {/* Storage quota */}
          <div style={{ marginBottom: 16, padding: '12px 14px', background: '#f8f8fb', borderRadius: 10, border: '1px solid #eee' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>Хранилище</span>
              <span style={{ fontSize: 13, color: overLimit ? '#dc3545' : '#666', fontVariantNumeric: 'tabular-nums' }}>
                {storageMB(storage.used)} / {storageMB(storage.quota)} МБ
              </span>
            </div>
            <div style={{ height: 8, background: '#e8e6ec', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: `${storagePct}%`,
                background: overLimit ? '#dc3545' : 'linear-gradient(90deg, #7B2FF7, #E040A0)',
                transition: 'width 0.3s ease',
              }} />
            </div>
            <div style={{ fontSize: 11, color: '#888', marginTop: 6 }}>
              {overLimit
                ? '⚠️ Почти заполнено. Удалите старые файлы, чтобы освободить место.'
                : 'Фото, видео, файлы и аудио считаются в лимит'}
            </div>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 13, color: '#666', marginBottom: 6 }}>О себе</label>
            <textarea
              value={bio}
              onChange={e => setBio(e.target.value)}
              placeholder="Несколько слов о себе..."
              rows={3}
              style={{
                width: '100%', padding: '10px 16px',
                border: '1px solid #e1e5e9', borderRadius: 8,
                fontSize: 14, resize: 'vertical', outline: 'none',
                fontFamily: 'inherit',
              }}
            />
          </div>
        </S.ModalBody>
        <S.ModalFooter>
          <S.ModalButton onClick={onLogout} style={{ background: '#fff0f0', color: '#dc3545', marginRight: 'auto' }}>
            <LogOut size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            Выйти
          </S.ModalButton>
          <S.ModalButton onClick={onClose}>Отмена</S.ModalButton>
          <S.ModalButton $primary disabled={saving || !displayName.trim()} onClick={handleSave}>
            {saving ? 'Сохранение...' : 'Сохранить'}
          </S.ModalButton>
        </S.ModalFooter>
      </S.Modal>
    </S.ModalOverlay>
  );
}

// ── New Chat Modal ──────────────────────────────────────────
function NewChatModal({ onClose, onCreateRoom }) {
  const [type, setType] = useState('direct');
  const [query, setQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [groupName, setGroupName] = useState('');

  const searchUsers = async (q) => {
    if (q.length < 2) { setUsers([]); return; }
    try { const { data } = await api.get('/users/search', { params: { q } }); if (data.success) setUsers(data.users); } catch {}
  };

  const toggleUser = (u) => setSelected(prev => prev.find(s => s.id === u.id) ? prev.filter(s => s.id !== u.id) : [...prev, u]);

  const handleCreate = async () => {
    if (type === 'direct' && selected.length === 1) { await onCreateRoom(null, 'direct', [selected[0].id]); onClose(); }
    else if (type === 'group' && groupName.trim() && selected.length > 0) { await onCreateRoom(groupName.trim(), 'group', selected.map(u => u.id)); onClose(); }
  };

  return (
    <S.ModalOverlay onClick={onClose}>
      <S.Modal onClick={e => e.stopPropagation()}>
        <S.ModalHeader>Новый чат<S.HeaderActionButton onClick={onClose} style={{ background: 'none', color: '#666' }}><X size={18} /></S.HeaderActionButton></S.ModalHeader>
        <S.ModalBody>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <S.ModalButton $primary={type === 'direct'} onClick={() => setType('direct')}>Личный</S.ModalButton>
            <S.ModalButton $primary={type === 'group'} onClick={() => setType('group')}>Группа</S.ModalButton>
          </div>
          {type === 'group' && <S.ModalInput placeholder="Название группы" value={groupName} onChange={e => setGroupName(e.target.value)} />}
          {selected.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 12 }}>
              {selected.map(u => <span key={u.id} onClick={() => toggleUser(u)} style={{ background: 'rgba(25,118,210,0.1)', padding: '3px 10px', borderRadius: 12, fontSize: 13, cursor: 'pointer', color: '#7B2FF7' }}>{u.displayName} ✕</span>)}
            </div>
          )}
          <S.ModalInput placeholder="Поиск пользователей..." value={query} onChange={e => { setQuery(e.target.value); searchUsers(e.target.value); }} />
          <S.UserList>
            {users.map(u => (
              <S.UserCheckbox key={u.id} onClick={() => type === 'direct' ? setSelected([u]) : toggleUser(u)}>
                {type === 'group' && <input type="checkbox" checked={selected.some(s => s.id === u.id)} readOnly />}
                <span>{u.displayName} <span style={{ color: '#888', marginLeft: 8, fontSize: 12 }}>@{u.username}</span></span>
              </S.UserCheckbox>
            ))}
            {users.length === 0 && query.length >= 2 && <p style={{ padding: 16, color: '#888', textAlign: 'center', margin: 0 }}>Не найдено</p>}
          </S.UserList>
        </S.ModalBody>
        <S.ModalFooter>
          <S.ModalButton onClick={onClose}>Отмена</S.ModalButton>
          <S.ModalButton $primary disabled={selected.length === 0 || (type === 'group' && !groupName.trim())} onClick={handleCreate}>
            {type === 'direct' ? 'Начать чат' : `Создать группу (${selected.length})`}
          </S.ModalButton>
        </S.ModalFooter>
      </S.Modal>
    </S.ModalOverlay>
  );
}
